import { Alert } from 'src/design/atoms/Alert';
import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Accordion } from 'src/design/molecules/Accordion';
import { Table } from 'src/design/molecules/Table';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import React from 'react';
import {
  FailedImportRow,
  SuccessfulImportRow,
  UserRateImportResult,
} from 'src/api/FiretrolApiClient';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';

interface ImportResultsProps {
  result: UserRateImportResult;
  onClose: () => void;
  onRetry: () => void;
}

export const ImportResults: React.FC<ImportResultsProps> = ({
  result,
  onClose,
  onRetry,
}) => {
  const successCount = result.successfulRows.length;
  const failedCount = result.failedRows.length;
  const totalCount = result.totalRowsProcessed;

  const hasSuccess = successCount > 0;
  const hasFailures = failedCount > 0;

  return (
    <Stack gap="md">
      <Alert
        icon={
          hasFailures ? <IconAlertCircle size={16} /> : <IconCheck size={16} />
        }
        title={
          hasFailures ? 'Import Completed with Errors' : 'Import Successful'
        }
        color={hasFailures ? 'yellow' : 'green'}
        variant="light"
      >
        <Stack gap="xs">
          <Text size="sm">
            Total rows processed: <strong>{totalCount}</strong>
          </Text>
          {hasSuccess && (
            <Text size="sm" c="green">
              Successfully imported: <strong>{successCount}</strong>
            </Text>
          )}
          {hasFailures && (
            <Text size="sm" c="red">
              Failed to import: <strong>{failedCount}</strong>
            </Text>
          )}
        </Stack>
      </Alert>

      {hasSuccess && (
        <Accordion variant="separated">
          <Accordion.Item value="successful">
            <Accordion.Control>
              <Group gap="xs">
                <IconCheck size={16} color="green" />
                <Text size="sm" fw={500}>
                  Successful Imports
                </Text>
                <Badge color="green" variant="light" size="sm">
                  {successCount}
                </Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Row #</Table.Th>
                    <Table.Th>User ID</Table.Th>
                    <Table.Th>Name</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {result.successfulRows.map((row: SuccessfulImportRow) => (
                    <Table.Tr key={`${row.rowNumber}-${row.userId}`}>
                      <Table.Td>{row.rowNumber}</Table.Td>
                      <Table.Td>{row.userId}</Table.Td>
                      <Table.Td>{row.name}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}

      {hasFailures && (
        <Accordion variant="separated" defaultValue="failed">
          <Accordion.Item value="failed">
            <Accordion.Control>
              <Group gap="xs">
                <IconX size={16} color="red" />
                <Text size="sm" fw={500}>
                  Failed Imports
                </Text>
                <Badge color="red" variant="light" size="sm">
                  {failedCount}
                </Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Row #</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Errors</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {result.failedRows.map((row: FailedImportRow) => (
                    <Table.Tr key={row.rowNumber}>
                      <Table.Td>{row.rowNumber}</Table.Td>
                      <Table.Td>{row.name || '(No name provided)'}</Table.Td>
                      <Table.Td>
                        <Stack gap="xs">
                          {row.errors.map((error, idx) => (
                            <Text key={idx} size="sm" c="red">
                              {error}
                            </Text>
                          ))}
                        </Stack>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}

      <Group justify="flex-end">
        {hasFailures && (
          <BasicButton variant="light" onClick={onRetry}>
            Upload Another File
          </BasicButton>
        )}
        <SecondaryButton onClick={onClose}>
          {hasFailures ? 'Close' : 'Done'}
        </SecondaryButton>
      </Group>
    </Stack>
  );
};
